import { useIntl, FormattedMessage, defineMessage } from 'react-intl';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import { PlaceholderMap, Button, ScreenElement } from '@micromag/core/components';
import { useGoogleKeys, useScreenSize, usePlaybackContext, usePlaybackMediaRef, useScreenRenderContext, useScreenState, useViewerInteraction } from '@micromag/core/contexts';
import { useTrackScreenEvent, useDimensionObserver } from '@micromag/core/hooks';
import { getStyleFromColor, isTextFilled } from '@micromag/core/utils';
import { Background } from '@micromag/element-background';
import ButtonElement from '@micromag/element-button';
import Container from '@micromag/element-container';
import Heading from '@micromag/element-heading';
import ImageElement from '@micromag/element-image';
import Map from '@micromag/element-map';
import Scroll from '@micromag/element-scroll';
import Text from '@micromag/element-text';
import _objectDestructuringEmpty from '@babel/runtime/helpers/objectDestructuringEmpty';
import _extends from '@babel/runtime/helpers/extends';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import { Map as Map$1, Container as Container$1 } from '@micromag/transforms/apple-news';

var styles = {"container":"micromag-screen-map-container","splash":"micromag-screen-map-splash","markerOverlayScrollable":"micromag-screen-map-markerOverlayScrollable","markerOverlayContainer":"micromag-screen-map-markerOverlayContainer","background":"micromag-screen-map-background","disabled":"micromag-screen-map-disabled","hidden":"micromag-screen-map-hidden","placeholder":"micromag-screen-map-placeholder","content":"micromag-screen-map-content","markerOverlaySafe":"micromag-screen-map-markerOverlaySafe","markerOverlay":"micromag-screen-map-markerOverlay","markerOverlayContent":"micromag-screen-map-markerOverlayContent","markerOverlayContentInner":"micromag-screen-map-markerOverlayContentInner","swipeIndicator":"micromag-screen-map-swipeIndicator","title":"micromag-screen-map-title","description":"micromag-screen-map-description","button":"micromag-screen-map-button","emptyTitle":"micromag-screen-map-emptyTitle","emptyDescription":"micromag-screen-map-emptyDescription","emptyButton":"micromag-screen-map-emptyButton","splashButton":"micromag-screen-map-splashButton","closeButton":"micromag-screen-map-closeButton","markerImage":"micromag-screen-map-markerImage","markerTitle":"micromag-screen-map-markerTitle","markerSubtitle":"micromag-screen-map-markerSubtitle","markerDescription":"micromag-screen-map-markerDescription","hasSelectedMarker":"micromag-screen-map-hasSelectedMarker","opened":"micromag-screen-map-opened"};

var defaultCenter = {
  lat: 45.5,
  lng: -73.56
};
var defaultZoom = 10;
var stopDragEventsPropagation = {
  onTouchMove: function onTouchMove(e) {
    return e.stopPropagation();
  },
  onTouchStart: function onTouchStart(e) {
    return e.stopPropagation();
  },
  onTouchEnd: function onTouchEnd(e) {
    return e.stopPropagation();
  },
  onPointerMove: function onPointerMove(e) {
    return e.stopPropagation();
  },
  onPointerUp: function onPointerUp(e) {
    return e.stopPropagation();
  },
  onPointerDown: function onPointerDown(e) {
    return e.stopPropagation();
  }
};
var propTypes = {
  layout: PropTypes.oneOf(['normal']),
  draggable: PropTypes.bool,
  markers: PropTypes.oneOfType([PropTypes$1.markers, PropTypes$1.markersWithImage]),
  title: PropTypes$1.textElement,
  description: PropTypes$1.textElement,
  button: PropTypes$1.textElement,
  openedMarkerSpacerHeight: PropTypes.number,
  withMarkerImages: PropTypes.bool,
  center: PropTypes$1.geoPosition,
  zoom: PropTypes.number,
  fitBounds: PropTypes.bool,
  // header: MicromagPropTypes.header,
  // footer: MicromagPropTypes.footer,
  background: PropTypes$1.backgroundElement,
  current: PropTypes.bool,
  active: PropTypes.bool,
  // enableInteractions: PropTypes.func,
  // disableInteraction: PropTypes.func,
  type: PropTypes.string,
  className: PropTypes.string
};
var defaultProps = {
  layout: 'normal',
  draggable: true,
  markers: [],
  center: defaultCenter,
  zoom: defaultZoom,
  fitBounds: true,
  title: null,
  description: null,
  button: null,
  openedMarkerSpacerHeight: 0.75,
  withMarkerImages: false,
  background: null,
  current: true,
  active: true,
  type: null,
  className: null
};
function MapScreen(_ref) {
  var layout = _ref.layout,
    draggable = _ref.draggable,
    markers = _ref.markers,
    title = _ref.title,
    description = _ref.description,
    button = _ref.button,
    openedMarkerSpacerHeight = _ref.openedMarkerSpacerHeight,
    withMarkerImages = _ref.withMarkerImages,
    zoom = _ref.zoom,
    center = _ref.center,
    fitBounds = _ref.fitBounds,
    background = _ref.background,
    current = _ref.current,
    active = _ref.active,
    type = _ref.type,
    className = _ref.className;
  var _useIntl = useIntl(),
    locale = _useIntl.locale;
  var _useGoogleKeys = useGoogleKeys(),
    _useGoogleKeys$apiKey = _useGoogleKeys.apiKey,
    apiKey = _useGoogleKeys$apiKey === void 0 ? null : _useGoogleKeys$apiKey;
  var trackScreenEvent = useTrackScreenEvent(type);
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    selectedMarkerIndex = _useState2[0],
    setSelectedMarkerIndex = _useState2[1];
  var hasSelectedMarker = selectedMarkerIndex !== null;
  var lastRenderedMarker = useRef(null);
  var _useScreenSize = useScreenSize(),
    width = _useScreenSize.width,
    height = _useScreenSize.height,
    resolution = _useScreenSize.resolution;
  var _usePlaybackContext = usePlaybackContext(),
    muted = _usePlaybackContext.muted;
  var mediaRef = usePlaybackMediaRef(current);
  var _ref2 = background || {},
    backgroundColor = _ref2.color;
  var markerOverlayContentStyle = getStyleFromColor(backgroundColor);
  var _useScreenRenderConte = useScreenRenderContext(),
    isView = _useScreenRenderConte.isView,
    isPreview = _useScreenRenderConte.isPreview,
    isPlaceholder = _useScreenRenderConte.isPlaceholder,
    isEdit = _useScreenRenderConte.isEdit,
    isStatic = _useScreenRenderConte.isStatic,
    isCapture = _useScreenRenderConte.isCapture;
  var screenState = useScreenState();
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    ready = _useState4[0],
    setReady = _useState4[1];
  var transitionDisabled = isStatic || isCapture || isPlaceholder || isPreview || isEdit;
  var scrollingDisabled = !isEdit && transitionDisabled || !current;
  var backgroundPlaying = current && (isView || isEdit);
  // const backgroundShouldLoad = current || active;
  var backgroundShouldLoad = current || active;
  var _useState5 = useState(isStatic || isCapture),
    _useState6 = _slicedToArray(_useState5, 2),
    opened = _useState6[0],
    setOpened = _useState6[1];
  var _useViewerInteraction = useViewerInteraction(),
    enableInteraction = _useViewerInteraction.enableInteraction,
    disableInteraction = _useViewerInteraction.disableInteraction;
  var onMapReady = useCallback(function () {
    return setReady(true);
  }, [setReady]);
  var finalMarkers = useMemo(function () {
    return (markers || []).map(function (marker, markerI) {
      return _objectSpread(_objectSpread({}, marker), {}, {
        active: markerI === selectedMarkerIndex
      });
    });
  }, [markers, selectedMarkerIndex]);
  var closeMarker = useCallback(function () {
    var lastMarker = finalMarkers[selectedMarkerIndex];
    lastRenderedMarker.current = lastMarker;
    setSelectedMarkerIndex(null);
  }, [finalMarkers, selectedMarkerIndex, setSelectedMarkerIndex]);
  var onClickMap = useCallback(function () {
    var lastMarker = finalMarkers[selectedMarkerIndex];
    trackScreenEvent('click_marker_close', "Marker ".concat(selectedMarkerIndex + 1, ": ").concat(lastMarker.title.body), {
      marker: lastMarker,
      markerIndex: selectedMarkerIndex
    });
    closeMarker();
  }, [finalMarkers, selectedMarkerIndex, trackScreenEvent, closeMarker]);
  var onClickMarker = useCallback(function (e, index) {
    var marker = finalMarkers[index];
    setSelectedMarkerIndex(index);
    trackScreenEvent('click_marker_open', "Marker ".concat(index + 1, ": ").concat(marker.title.body), {
      marker: marker,
      markerIndex: index
    });
  }, [finalMarkers, setSelectedMarkerIndex, trackScreenEvent]);
  var onButtonClick = useCallback(function () {
    setOpened(true);
    if (disableInteraction !== null) {
      disableInteraction();
    }
    trackScreenEvent('click_button', button.body);
  }, [setOpened, disableInteraction, trackScreenEvent, button]);
  var onCloseClick = useCallback(function () {
    setOpened(false);
    if (enableInteraction !== null) {
      enableInteraction();
    }
    trackScreenEvent('click_close', 'Close icon');
    closeMarker();
  }, [setOpened, enableInteraction, trackScreenEvent]);
  var onMapDragEnd = useCallback(function (newCenter) {
    var coords = newCenter.toJSON();
    trackScreenEvent('drag_map', "Latitude: ".concat(coords.lat.toFixed(4), ", Longitude: ").concat(coords.lng.toFixed(4)), {
      coords: coords
    });
  }, [trackScreenEvent]);
  var onScrolledBottom = useCallback(function (_ref3) {
    var initial = _ref3.initial;
    if (initial) {
      var selectedMarker = (markers || [])[selectedMarkerIndex];
      trackScreenEvent('scroll', "Marker ".concat(selectedMarkerIndex + 1, ": ").concat(selectedMarker.title.body), {
        marker: selectedMarker,
        markerIndex: selectedMarkerIndex
      });
    }
  }, [trackScreenEvent, markers, selectedMarkerIndex]);
  var _useDimensionObserver = useDimensionObserver({
      disabled: !isView
    }),
    markerOverContentInnerRef = _useDimensionObserver.ref,
    _useDimensionObserver2 = _useDimensionObserver.width,
    markerOverContentInnerWidth = _useDimensionObserver2 === void 0 ? '100%' : _useDimensionObserver2;
  var _useState7 = useState(0),
    _useState8 = _slicedToArray(_useState7, 2),
    markerImagesLoaded = _useState8[0],
    setMarkerImagesLoaded = _useState8[1];
  var allMarkersImagesLoaded = markerImagesLoaded === (markers || []).length;
  var finalReady = ready && (!withMarkerImages || allMarkersImagesLoaded);
  useEffect(function () {
    if (withMarkerImages && markers !== null && (markers || []).length) {
      setMarkerImagesLoaded(0);
      var imgs = markers.map(function (marker) {
        var _ref4 = marker || {},
          _ref4$image = _ref4.image,
          image = _ref4$image === void 0 ? null : _ref4$image;
        var _ref5 = image || {},
          _ref5$url = _ref5.url,
          url = _ref5$url === void 0 ? null : _ref5$url;
        var withUrl = url !== null;
        if (withUrl) {
          var img = new Image();
          img.src = url;
          img.onload = function () {
            setMarkerImagesLoaded(function (index) {
              return setMarkerImagesLoaded(index + 1);
            });
          };
          return img;
        }
        return null;
      });
      return function () {
        imgs.filter(function (img) {
          return img !== null;
        }).forEach(function (img) {
          img.onload = function () {}; // eslint-disable-line no-param-reassign
        });
      };
    }

    return function () {};
  }, [withMarkerImages, markers]);

  // Switch state
  useEffect(function () {
    if (!isEdit && !isPreview) {
      return;
    }
    var _ref6 = screenState !== null ? screenState.split('.') : [],
      _ref7 = _slicedToArray(_ref6, 2),
      _ref7$ = _ref7[0],
      stateId = _ref7$ === void 0 ? null : _ref7$,
      _ref7$2 = _ref7[1],
      markerIndex = _ref7$2 === void 0 ? null : _ref7$2;
    if (stateId === 'intro') {
      setOpened(false);
      setSelectedMarkerIndex(null);
    } else if (stateId === 'map') {
      setOpened(true);
      setSelectedMarkerIndex(null);
    } else if (stateId === 'markers' || stateId === 'markers-with-image') {
      setOpened(true);
      setSelectedMarkerIndex(parseInt(markerIndex, 10));
    }
  }, [screenState, isEdit, setOpened]);
  var staticUrl;
  if (width > 0 && height > 0 && apiKey !== null) {
    var correctMarkers = markers !== null ? markers.filter(function (it) {
      return it !== null;
    }).filter(function (_ref8) {
      var _ref8$geoPosition = _ref8.geoPosition,
        geoPosition = _ref8$geoPosition === void 0 ? null : _ref8$geoPosition;
      return geoPosition !== null;
    }) : null;
    staticUrl = "https://maps.googleapis.com/maps/api/staticmap?size=".concat(Math.round(width), "x").concat(Math.round(height));
    if (center !== null && (correctMarkers === null || correctMarkers.length === 0)) {
      var _ref9 = center || {},
        _ref9$lat = _ref9.lat,
        lat = _ref9$lat === void 0 ? null : _ref9$lat,
        _ref9$lng = _ref9.lng,
        lng = _ref9$lng === void 0 ? null : _ref9$lng;
      staticUrl += "&center=".concat(lat, ",").concat(lng);
    }
    if (zoom !== null) {
      staticUrl += "&zoom=".concat(zoom);
    }
    if (apiKey !== null) {
      staticUrl += "&key=".concat(apiKey);
    }
    if (locale !== null) {
      staticUrl += "&language=".concat(locale);
    }
    if (correctMarkers !== null) {
      staticUrl += correctMarkers.map(function (marker) {
        var _ref10 = marker || {},
          _ref10$geoPosition = _ref10.geoPosition,
          geoPosition = _ref10$geoPosition === void 0 ? null : _ref10$geoPosition;
        var _ref11 = geoPosition || {},
          _ref11$lat = _ref11.lat,
          lat = _ref11$lat === void 0 ? null : _ref11$lat,
          _ref11$lng = _ref11.lng,
          lng = _ref11$lng === void 0 ? null : _ref11$lng;
        var _ref12 = marker || {},
          _ref12$image = _ref12.image,
          image = _ref12$image === void 0 ? null : _ref12$image;
        var _ref13 = image || {},
          _ref13$url = _ref13.url,
          url = _ref13$url === void 0 ? null : _ref13$url;
        return lat !== null && lng !== null ? "&markers=".concat(url !== null ? "icon:".concat(url) : '', "%7C").concat(lat, ",").concat(lng) : '';
      }).join('');
    }
  }
  var renderedMarker = hasSelectedMarker ? finalMarkers[selectedMarkerIndex] : lastRenderedMarker.current;
  var _ref14 = renderedMarker || {},
    _ref14$title = _ref14.title,
    markerTitle = _ref14$title === void 0 ? null : _ref14$title,
    _ref14$subtitle = _ref14.subtitle,
    markerSubtitle = _ref14$subtitle === void 0 ? null : _ref14$subtitle,
    _ref14$description = _ref14.description,
    markerDescription = _ref14$description === void 0 ? null : _ref14$description,
    _ref14$image = _ref14.image,
    markerImage = _ref14$image === void 0 ? null : _ref14$image;
  var hasMarkerTitle = markerTitle !== null;
  var hasMarkerSubtitle = markerSubtitle !== null;
  var hasMarkerDescription = markerDescription !== null;
  var hasMarkerImage = markerImage !== null;
  var hasTitle = isTextFilled(title);
  var hasDescription = isTextFilled(description);
  var hasButton = isTextFilled(button);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty(_defineProperty(_defineProperty(_defineProperty({}, className, className !== null), styles["".concat(layout, "Layout")], layout !== null), styles.opened, opened || isPreview && screenState !== 'intro'), styles.hasSelectedMarker, hasSelectedMarker)]),
    "data-screen-ready": finalReady
  }, /*#__PURE__*/React.createElement(Container, {
    width: width,
    height: height,
    className: styles.content
  }, isPlaceholder ? /*#__PURE__*/React.createElement(PlaceholderMap, {
    className: styles.placeholder,
    withImages: withMarkerImages
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, isPreview ? /*#__PURE__*/React.createElement(ImageElement, {
    media: staticUrl !== null ? {
      url: staticUrl,
      metadata: {
        width: Math.min(640, width),
        height: Math.min(640, height)
      }
    } : null,
    width: width,
    height: height,
    resolution: resolution,
    objectFit: {
      fit: 'cover'
    }
  }) : /*#__PURE__*/React.createElement(Map, {
    key: "map",
    center: center,
    zoom: zoom,
    draggable: draggable,
    markers: finalMarkers,
    fitBounds: fitBounds,
    onClickMarker: onClickMarker,
    onReady: onMapReady,
    onDragEnd: onMapDragEnd
  }), /*#__PURE__*/React.createElement("div", Object.assign({
    key: "marker-overlay",
    className: styles.markerOverlayContainer
  }, stopDragEventsPropagation), /*#__PURE__*/React.createElement("div", {
    className: styles.markerOverlayScrollable
  }, /*#__PURE__*/React.createElement(Scroll, {
    fullscreen: true,
    disabled: scrollingDisabled,
    onScrolledBottom: onScrolledBottom
  }, /*#__PURE__*/React.createElement(Button, {
    className: styles.markerOverlaySafe,
    onClick: onClickMap,
    withoutStyle: true,
    style: {
      height: height * openedMarkerSpacerHeight
    },
    disabled: isPreview,
    focusable: current && isView
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.markerOverlay,
    style: {
      minHeight: height * (1 - openedMarkerSpacerHeight)
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.markerOverlayContent,
    style: markerOverlayContentStyle
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.swipeIndicator
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.markerOverlayContentInner,
    key: "markerContent-".concat(selectedMarkerIndex),
    ref: markerOverContentInnerRef
  }, hasMarkerImage ? /*#__PURE__*/React.createElement(ImageElement, {
    className: styles.markerImage,
    media: markerImage,
    width: markerOverContentInnerWidth,
    resolution: resolution
  }) : null, hasMarkerTitle ? /*#__PURE__*/React.createElement(Heading, Object.assign({
    className: styles.markerTitle
  }, markerTitle)) : null, hasMarkerSubtitle ? /*#__PURE__*/React.createElement(Heading, Object.assign({
    size: 3,
    className: styles.markerSubtitle
  }, markerSubtitle)) : null, hasMarkerDescription ? /*#__PURE__*/React.createElement(Text, Object.assign({
    className: styles.markerDescription
  }, markerDescription)) : null)))))), /*#__PURE__*/React.createElement("div", {
    key: "splash",
    className: classNames([styles.splash])
  }, /*#__PURE__*/React.createElement(ScreenElement, {
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "2ZOPe+",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    }),
    emptyClassName: styles.emptyTitle,
    isEmpty: !hasTitle
  }, /*#__PURE__*/React.createElement(Heading, Object.assign({
    className: styles.title
  }, title))), /*#__PURE__*/React.createElement(ScreenElement, {
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "507VAi",
      defaultMessage: [{
        "type": 0,
        "value": "Description"
      }]
    }),
    emptyClassName: styles.emptyDescription,
    isEmpty: !hasDescription
  }, /*#__PURE__*/React.createElement(Text, Object.assign({
    className: styles.description
  }, description))), /*#__PURE__*/React.createElement(ScreenElement, {
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "bv3rRe",
      defaultMessage: [{
        "type": 0,
        "value": "Button"
      }]
    }),
    emptyClassName: styles.emptyButton,
    isEmpty: !hasButton
  }, /*#__PURE__*/React.createElement(ButtonElement, {
    className: styles.splashButton,
    onClick: onButtonClick,
    buttonStyle: button !== null ? button.buttonStyle : null,
    focusable: current && isView
  }, hasButton ? /*#__PURE__*/React.createElement(Text, Object.assign({}, button, {
    className: styles.button
  })) : /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "sXfFFw",
    defaultMessage: [{
      "type": 0,
      "value": "Enter"
    }]
  })))), !isStatic && !isCapture && !isPreview && !isEdit ? /*#__PURE__*/React.createElement(Button, {
    key: "close-button",
    className: styles.closeButton,
    onClick: onCloseClick,
    focusable: current && isView
  }, "\xD7") : null)), !isPlaceholder ? /*#__PURE__*/React.createElement(Background, {
    color: {
      color: '#FFFFFF',
      alpha: 1
    },
    width: width,
    height: height,
    resolution: resolution,
    playing: backgroundPlaying,
    muted: muted,
    shouldLoad: backgroundShouldLoad,
    mediaRef: mediaRef,
    withoutVideo: isPreview,
    className: styles.background
  }) : null);
}
MapScreen.propTypes = propTypes;
MapScreen.defaultProps = defaultProps;

var MapImagesScreen = function MapImagesScreen(_ref) {
  var props = _extends({}, (_objectDestructuringEmpty(_ref), _ref));
  return /*#__PURE__*/React.createElement(MapScreen, Object.assign({}, props, {
    withMarkerImages: true
  }));
};

var transform = function transform(newStory, _ref) {
  var markers = _ref.markers;
  var marker = markers.find(function (_ref2) {
    var _ref2$geoPosition = _ref2.geoPosition,
      geoPosition = _ref2$geoPosition === void 0 ? null : _ref2$geoPosition;
    return geoPosition !== null && geoPosition.latitude !== null && geoPosition.longitude !== null;
  });
  var _ref3 = marker || {},
    _ref3$geoPosition = _ref3.geoPosition,
    geoPosition = _ref3$geoPosition === void 0 ? {} : _ref3$geoPosition;
  var _ref4 = geoPosition || {},
    _ref4$lat = _ref4.lat,
    lat = _ref4$lat === void 0 ? null : _ref4$lat,
    _ref4$lng = _ref4.lng,
    lng = _ref4$lng === void 0 ? null : _ref4$lng;
  var _Map = Map$1(newStory, {
      latitude: lat,
      longitude: lng
    }),
    titleStory = _Map.story,
    titleComponent = _Map.component;
  var _Container = Container$1(titleStory, _toConsumableArray(titleComponent ? [titleComponent] : [])),
    containerStory = _Container.story,
    containerComponent = _Container.component;
  return _objectSpread(_objectSpread({}, containerStory), {}, {
    components: [].concat(_toConsumableArray(newStory.components || []), _toConsumableArray(containerComponent ? [containerComponent] : []))
  });
};

// eslint-disable-next-line

var transforms = /*#__PURE__*/Object.freeze({
    __proto__: null,
    appleNews: transform
});

var definition = [{
  id: 'map',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "Z27IP6",
      defaultMessage: [{
        "type": 0,
        "value": "Map"
      }]
    }),
    order: 6
  },
  title: defineMessage({
    id: "dPYwJG",
    defaultMessage: [{
      "type": 0,
      "value": "Map"
    }]
  }),
  component: MapScreen,
  layouts: ['normal'],
  transforms: transforms,
  states: [{
    id: 'intro',
    label: defineMessage({
      id: "BgrRxZ",
      defaultMessage: [{
        "type": 0,
        "value": "Intro"
      }]
    }),
    fields: [{
      name: 'title',
      type: 'heading-element',
      theme: {
        textStyle: 'heading1'
      },
      label: defineMessage({
        id: "+AEVbJ",
        defaultMessage: [{
          "type": 0,
          "value": "Title"
        }]
      })
    }, {
      name: 'description',
      type: 'text-element',
      theme: {
        textStyle: 'text'
      },
      label: defineMessage({
        id: "ZCe0r4",
        defaultMessage: [{
          "type": 0,
          "value": "Description"
        }]
      })
    }, {
      name: 'button',
      type: 'button-element',
      theme: {
        textStyle: 'button'
      },
      label: defineMessage({
        id: "i6bmbD",
        defaultMessage: [{
          "type": 0,
          "value": "Button"
        }]
      })
    }]
  }, {
    id: 'map',
    label: defineMessage({
      id: "eYHEYe",
      defaultMessage: [{
        "type": 0,
        "value": "Map"
      }]
    }),
    fields: [{
      name: 'draggable',
      type: 'toggle',
      defaultValue: true,
      label: defineMessage({
        id: "A7ncT3",
        defaultMessage: [{
          "type": 0,
          "value": "Draggable"
        }]
      })
    }, {
      name: 'fitBounds',
      type: 'toggle',
      defaultValue: true,
      label: defineMessage({
        id: "NVUpOG",
        defaultMessage: [{
          "type": 0,
          "value": "Map fit markers"
        }]
      })
    }, {
      name: 'zoom',
      type: 'slider',
      defaultValue: 9,
      min: 0,
      max: 16,
      label: defineMessage({
        id: "vRzzo6",
        defaultMessage: [{
          "type": 0,
          "value": "Zoom"
        }]
      })
    }, {
      name: 'center',
      type: 'geo-position',
      label: defineMessage({
        id: "W5qWPj",
        defaultMessage: [{
          "type": 0,
          "value": "Center"
        }]
      })
    }]
  }, {
    id: 'markers',
    label: defineMessage({
      id: "+LvOvJ",
      defaultMessage: [{
        "type": 0,
        "value": "Markers"
      }]
    }),
    repeatable: true,
    fieldName: 'markers',
    fields: [{
      name: 'title',
      type: 'heading-element',
      label: defineMessage({
        id: "+AEVbJ",
        defaultMessage: [{
          "type": 0,
          "value": "Title"
        }]
      })
    }, {
      name: 'description',
      type: 'text-element',
      label: defineMessage({
        id: "ZCe0r4",
        defaultMessage: [{
          "type": 0,
          "value": "Description"
        }]
      })
    }, {
      name: 'geoPosition',
      type: 'geo-position',
      label: defineMessage({
        id: "u3ok54",
        defaultMessage: [{
          "type": 0,
          "value": "Position"
        }]
      })
    }]
  }],
  fields: [{
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "cDj1mZ",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }]
}, {
  id: 'map-images',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "Z27IP6",
      defaultMessage: [{
        "type": 0,
        "value": "Map"
      }]
    }),
    order: 6
  },
  title: defineMessage({
    id: "ro6yHS",
    defaultMessage: [{
      "type": 0,
      "value": "Map with image markers"
    }]
  }),
  component: MapImagesScreen,
  layouts: ['normal'],
  transforms: transforms,
  states: [{
    id: 'intro',
    label: defineMessage({
      id: "BgrRxZ",
      defaultMessage: [{
        "type": 0,
        "value": "Intro"
      }]
    }),
    fields: [{
      name: 'title',
      type: 'heading-element',
      theme: {
        textStyle: 'heading1'
      },
      label: defineMessage({
        id: "+AEVbJ",
        defaultMessage: [{
          "type": 0,
          "value": "Title"
        }]
      })
    }, {
      name: 'description',
      type: 'text-element',
      theme: {
        textStyle: 'text'
      },
      label: defineMessage({
        id: "ZCe0r4",
        defaultMessage: [{
          "type": 0,
          "value": "Description"
        }]
      })
    }, {
      name: 'button',
      type: 'button-element',
      theme: {
        textStyle: 'button'
      },
      label: defineMessage({
        id: "i6bmbD",
        defaultMessage: [{
          "type": 0,
          "value": "Button"
        }]
      })
    }]
  }, {
    id: 'map',
    label: defineMessage({
      id: "eYHEYe",
      defaultMessage: [{
        "type": 0,
        "value": "Map"
      }]
    }),
    fields: [{
      name: 'draggable',
      type: 'toggle',
      defaultValue: true,
      label: defineMessage({
        id: "A7ncT3",
        defaultMessage: [{
          "type": 0,
          "value": "Draggable"
        }]
      })
    }, {
      name: 'fitBounds',
      type: 'toggle',
      defaultValue: true,
      label: defineMessage({
        id: "NVUpOG",
        defaultMessage: [{
          "type": 0,
          "value": "Map fit markers"
        }]
      })
    }, {
      name: 'zoom',
      type: 'slider',
      defaultValue: 9,
      min: 0,
      max: 16,
      label: defineMessage({
        id: "vRzzo6",
        defaultMessage: [{
          "type": 0,
          "value": "Zoom"
        }]
      })
    }, {
      name: 'center',
      type: 'geo-position',
      label: defineMessage({
        id: "W5qWPj",
        defaultMessage: [{
          "type": 0,
          "value": "Center"
        }]
      })
    }]
  }, {
    id: 'markers-with-image',
    label: defineMessage({
      id: "+LvOvJ",
      defaultMessage: [{
        "type": 0,
        "value": "Markers"
      }]
    }),
    repeatable: true,
    fieldName: 'markers',
    fields: [{
      name: 'title',
      type: 'heading-element',
      label: defineMessage({
        id: "+AEVbJ",
        defaultMessage: [{
          "type": 0,
          "value": "Title"
        }]
      })
    }, {
      name: 'description',
      type: 'text-element',
      label: defineMessage({
        id: "ZCe0r4",
        defaultMessage: [{
          "type": 0,
          "value": "Description"
        }]
      })
    }, {
      name: 'geoPosition',
      type: 'geo-position',
      label: defineMessage({
        id: "u3ok54",
        defaultMessage: [{
          "type": 0,
          "value": "Position"
        }]
      })
    }]
  }],
  fields: [{
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "cDj1mZ",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }]
}];

export { MapImagesScreen, MapScreen, definition as default };
